// Tests Content - Explanation pages for common urologic tests
// Each test links to relevant condition articles in the platform

export const testsData = [
  {
    id: 'psa-blood-test',
    title: 'PSA Blood Test',
    icon: 'droplet',
    category: 'Blood Test',
    summary: 'A blood test that measures prostate-specific antigen, used for prostate cancer screening and monitoring.',
    content: {
      whatItMeasures: `The PSA (Prostate-Specific Antigen) test measures the level of PSA protein in your blood. PSA is produced by both normal and cancerous prostate cells.

**Key facts about PSA:**
- PSA is measured in nanograms per milliliter (ng/mL)
- Higher levels may indicate prostate problems
- PSA can be elevated for many reasons, not just cancer
- The test requires a simple blood draw

**Types of PSA tests:**
- **Total PSA** — measures all PSA in the blood
- **Free PSA** — measures PSA not bound to proteins (helps distinguish cancer from benign conditions)
- **PSA density** — PSA level divided by prostate volume
- **PSA velocity** — how fast PSA rises over time`,

      whyOrdered: `Doctors order PSA tests for several reasons:

**Prostate Cancer Screening**
- For men considering screening (typically ages 50-70, or earlier with risk factors)
- Family history of prostate cancer increases risk
- African American men have higher risk and may start screening earlier

**Monitoring Known Conditions**
- Tracking prostate cancer after treatment
- Monitoring men on active surveillance
- Following PSA trends in men with elevated levels

**Evaluating Symptoms**
- Urinary symptoms that could indicate prostate problems
- As part of a workup for an abnormal prostate exam

**After Prostate Cancer Treatment**
- Detecting recurrence after surgery or radiation
- PSA should become undetectable after prostatectomy`,

      resultsInterpretation: `**Traditional Reference Ranges:**

| PSA Level | Interpretation |
|-----------|----------------|
| 0-2.5 ng/mL | Generally considered normal |
| 2.5-4.0 ng/mL | Borderline (age-dependent) |
| 4.0-10 ng/mL | Mildly elevated — ~25% chance of cancer |
| >10 ng/mL | Significantly elevated — higher cancer risk |

**Important context:**
- There is NO single "normal" PSA — it varies by age
- PSA naturally increases with age and prostate size
- A man with a PSA of 3.0 at age 50 may warrant more concern than 3.0 at age 70

**Factors that can elevate PSA (besides cancer):**
- Benign prostatic hyperplasia (BPH) — enlarged prostate
- Prostatitis — prostate infection or inflammation
- Recent ejaculation (within 24-48 hours)
- Urinary tract infection
- Recent prostate biopsy or procedure
- Vigorous cycling

**Free PSA ratio:**
- Free PSA >25% — more likely benign
- Free PSA <10% — more concerning for cancer

**What an abnormal result does NOT mean:**
- An elevated PSA does NOT automatically mean you have cancer
- Most men with elevated PSA do NOT have prostate cancer
- Further evaluation is needed to determine the cause`,

      nextSteps: `**If PSA is normal:**
- Continue routine screening per your doctor's recommendation
- Typical interval: every 1-2 years depending on level and risk

**If PSA is mildly elevated (4-10 ng/mL):**
- Repeat PSA to confirm (may have been temporarily elevated)
- Free PSA testing to refine risk assessment
- Prostate MRI may be recommended before biopsy
- Consider age, family history, and other risk factors
- Discussion about prostate biopsy

**If PSA is significantly elevated (>10 ng/mL):**
- Usually warrants further investigation
- Prostate MRI often performed
- Prostate biopsy typically recommended
- Additional staging tests if cancer is found

**After prostate cancer treatment:**
- PSA should be undetectable after prostatectomy
- Rising PSA after treatment may indicate recurrence
- Regular monitoring schedule (every 3-6 months initially)

**Important decision points:**
- Whether to proceed with biopsy is a shared decision
- Consider your overall health, life expectancy, and preferences
- Not all prostate cancers need treatment`,

      relatedConditions: [
        { title: 'Prostate Cancer (Screening & Localized Disease)', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'Understanding Active Surveillance for Prostate Cancer', articleId: '86764b75-bfe3-4065-9c7a-23f1c8d5dd25' },
        { title: 'Prostate Cancer Treatment Options', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' }
      ]
    }
  },

  {
    id: 'urinalysis',
    title: 'Urinalysis',
    icon: 'test-tube',
    category: 'Urine Test',
    summary: 'A simple urine test that checks for infections, blood, protein, and other abnormalities.',
    content: {
      whatItMeasures: `Urinalysis is a comprehensive urine test that evaluates multiple components of your urine through visual, chemical, and microscopic examination.

**Visual examination:**
- **Color** — normal is pale to dark yellow
- **Clarity** — should be clear, not cloudy

**Chemical (dipstick) testing:**
- **pH** — acidity/alkalinity of urine (normal: 4.5-8.0)
- **Specific gravity** — concentration of urine
- **Protein** — normally minimal
- **Glucose** — sugar (normally absent)
- **Blood** — red blood cells (normally absent)
- **Leukocyte esterase** — indicates white blood cells
- **Nitrites** — suggests bacterial infection
- **Bilirubin** — liver function indicator
- **Ketones** — related to metabolism

**Microscopic examination:**
- Red blood cells (RBCs)
- White blood cells (WBCs)
- Bacteria
- Crystals
- Casts (cylindrical structures from kidney tubules)
- Epithelial cells`,

      whyOrdered: `Urinalysis is one of the most commonly ordered tests in medicine:

**Routine screening:**
- Part of annual physical exams
- Pre-operative evaluation
- Pregnancy monitoring

**Diagnosing infections:**
- Suspected urinary tract infection (UTI)
- Kidney infection (pyelonephritis)
- Sexually transmitted infections

**Evaluating symptoms:**
- Blood in urine (hematuria)
- Painful urination
- Frequent urination
- Flank pain
- Cloudy or foul-smelling urine

**Monitoring conditions:**
- Kidney disease progression
- Diabetes management
- After urologic procedures

**Screening for kidney problems:**
- Detecting protein in urine (proteinuria)
- Identifying early kidney disease`,

      resultsInterpretation: `**Normal urinalysis results:**
- Color: Pale to dark yellow
- Clarity: Clear
- pH: 4.5-8.0
- Specific gravity: 1.005-1.030
- Protein: Negative or trace
- Glucose: Negative
- Blood: Negative
- Leukocyte esterase: Negative
- Nitrites: Negative
- WBCs: 0-5 per high power field
- RBCs: 0-3 per high power field
- Bacteria: None seen

**Abnormal findings and what they suggest:**

| Finding | Possible Causes |
|---------|-----------------|
| Blood (hematuria) | UTI, kidney stones, bladder cancer, trauma |
| White blood cells | Infection (UTI, kidney infection) |
| Nitrites positive | Bacterial infection (usually gram-negative) |
| Protein | Kidney disease, fever, exercise, dehydration |
| Glucose | Diabetes, pregnancy |
| Leukocyte esterase + | White blood cells present (infection) |
| Cloudy urine | Infection, crystals, contamination |
| Crystals | Kidney stone risk, certain medications |

**Important notes:**
- Contamination can cause false positives (especially in women)
- A single abnormal result often needs confirmation
- Results must be interpreted in clinical context`,

      nextSteps: `**If urinalysis is normal:**
- No further testing typically needed
- Continue routine health maintenance

**If UTI is suspected (WBCs, nitrites, bacteria):**
- Urine culture to identify specific bacteria
- Antibiotics prescribed based on results
- Follow-up urinalysis may confirm resolution

**If blood is found (hematuria):**
- Repeat urinalysis to confirm
- Urine culture to rule out infection
- Further workup may include:
  - CT urogram (imaging of urinary tract)
  - Cystoscopy (bladder examination)
  - Urine cytology (checking for cancer cells)

**If protein is elevated:**
- Repeat testing to confirm
- 24-hour urine collection may be ordered
- Blood tests to check kidney function
- Referral to nephrologist if persistent

**If glucose is present:**
- Blood glucose testing
- Hemoglobin A1c to screen for diabetes
- May indicate uncontrolled diabetes

**Clean catch instructions for accurate results:**
- Clean the genital area before collection
- Start urinating, then collect midstream
- Avoid touching the inside of the container`,

      relatedConditions: [
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' },
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { title: 'Bladder Cancer (Non-Muscle Invasive)', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' }
      ]
    }
  },

  {
    id: 'urine-culture',
    title: 'Urine Culture',
    icon: 'flask',
    category: 'Urine Test',
    summary: 'A laboratory test that identifies the specific bacteria causing a urinary tract infection and determines which antibiotics will be effective.',
    content: {
      whatItMeasures: `A urine culture grows and identifies bacteria present in your urine. It's the definitive test for diagnosing urinary tract infections.

**What the test determines:**
- **Presence of bacteria** — confirms infection
- **Type of bacteria** — identifies the specific organism
- **Colony count** — quantifies bacterial growth (measured in CFU/mL)
- **Antibiotic sensitivities** — shows which antibiotics will kill the bacteria

**Common bacteria identified:**
- *Escherichia coli (E. coli)* — causes 80-85% of UTIs
- *Klebsiella* species
- *Proteus* species
- *Enterococcus* species
- *Staphylococcus saprophyticus*
- *Pseudomonas aeruginosa*

**Colony count interpretation:**
- **≥100,000 CFU/mL** — significant infection
- **10,000-100,000 CFU/mL** — may be significant, depending on symptoms
- **<10,000 CFU/mL** — usually contamination or no infection`,

      whyOrdered: `Urine cultures are ordered in specific situations:

**When UTI is suspected:**
- Urinalysis shows signs of infection
- Symptoms of UTI (burning, frequency, urgency)
- Fever with urinary symptoms

**Complicated infections:**
- Male UTIs (always considered complicated)
- Pregnant women with UTI symptoms
- Catheter-associated infections
- Pyelonephritis (kidney infection)
- Recurrent UTIs

**When initial treatment fails:**
- Symptoms persist after antibiotics
- Need to identify antibiotic resistance

**Specific populations:**
- Before urologic procedures
- Immunocompromised patients
- Patients with kidney transplants
- Children with UTI

**NOT typically needed for:**
- Uncomplicated UTI in healthy young women (empiric treatment often sufficient)
- Asymptomatic bacteriuria in most populations`,

      resultsInterpretation: `**Negative culture (no growth):**
- No bacterial infection present
- Symptoms may be from other causes (viral, interstitial cystitis, STI)
- If symptoms persist, consider other testing

**Positive culture with sensitivity results:**

The report will show:
1. **Organism identified** — e.g., "Escherichia coli"
2. **Colony count** — e.g., ">100,000 CFU/mL"
3. **Sensitivity panel** — list of antibiotics tested

**Sensitivity categories:**
- **S (Sensitive)** — antibiotic should work
- **I (Intermediate)** — may work at higher doses
- **R (Resistant)** — antibiotic will NOT work

**Example report interpretation:**
| Antibiotic | Result |
|------------|--------|
| Ciprofloxacin | S |
| Nitrofurantoin | S |
| Trimethoprim-sulfa | R |
| Amoxicillin | R |

This means ciprofloxacin or nitrofurantoin would be effective, but trimethoprim-sulfa or amoxicillin would NOT work.

**Mixed flora or contamination:**
- Multiple organisms suggest improper collection
- Repeat with careful clean-catch technique`,

      nextSteps: `**If culture is negative:**
- UTI is unlikely
- Consider other diagnoses if symptoms persist
- STI testing may be indicated
- Interstitial cystitis workup in some cases

**If culture confirms infection:**
- Review antibiotic sensitivities
- Start or adjust antibiotics based on results
- If already on antibiotics, confirm they match sensitivities
- Complete the full antibiotic course

**Typical antibiotic courses:**
- Uncomplicated UTI: 3-7 days
- Complicated UTI: 7-14 days
- Kidney infection: 7-14 days
- Male UTI: 7-14 days (often longer)

**Follow-up cultures:**
- Not routinely needed if symptoms resolve
- Recommended for:
  - Persistent symptoms
  - Complicated infections
  - Pregnant women
  - Recurrent UTIs

**For recurrent infections:**
- Consider prophylactic antibiotics
- Evaluate for underlying causes
- May need imaging (CT, ultrasound)
- Cystoscopy in some cases

**Important timing notes:**
- Culture results take 24-48 hours
- Sensitivities may take an additional day
- Initial antibiotic choice is often empiric, then adjusted`,

      relatedConditions: [
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: '5 Evidence-Based Tips to Prevent UTIs', articleId: 'c3d178c0-1621-4339-965a-959fb8be711d' },
        { title: 'Suprapubic Catheter Care', articleId: '005b86f1-bd0d-4c25-a3b2-6fa9c5374032' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' }
      ]
    }
  },

  {
    id: 'prostate-mri',
    title: 'MRI of the Prostate',
    icon: 'scan',
    category: 'Imaging',
    summary: 'A detailed imaging study that helps detect prostate cancer and guides biopsy decisions.',
    content: {
      whatItMeasures: `Multiparametric MRI (mpMRI) of the prostate creates detailed images of the prostate gland using magnetic fields and radio waves — no radiation is involved.

**What prostate MRI evaluates:**
- **Prostate anatomy** — size, shape, and zones
- **Suspicious lesions** — areas that may be cancerous
- **Cancer extent** — if present, how far it has spread
- **Extraprostatic extension** — cancer beyond the prostate capsule
- **Seminal vesicle involvement** — spread to nearby structures
- **Lymph node assessment** — enlarged or suspicious nodes

**PI-RADS scoring system:**
MRI findings are reported using PI-RADS (Prostate Imaging Reporting and Data System):

| PI-RADS Score | Interpretation |
|---------------|----------------|
| 1 | Very low — clinically significant cancer highly unlikely |
| 2 | Low — clinically significant cancer unlikely |
| 3 | Intermediate — presence of cancer equivocal |
| 4 | High — clinically significant cancer likely |
| 5 | Very high — clinically significant cancer highly likely |

**Types of MRI sequences used:**
- **T2-weighted** — shows anatomy
- **Diffusion-weighted (DWI)** — detects dense cellular areas
- **Dynamic contrast-enhanced (DCE)** — shows blood flow patterns`,

      whyOrdered: `Prostate MRI is increasingly used in prostate cancer care:

**Before initial biopsy:**
- Elevated PSA with decision about biopsy
- Helps identify targets for biopsy
- May avoid unnecessary biopsies if no suspicious lesions
- Improves detection of clinically significant cancer

**After negative biopsy:**
- Persistent elevated or rising PSA
- Previous biopsy was negative but concern remains
- Identify areas missed by prior blind biopsy

**Staging known cancer:**
- Determine cancer extent before treatment
- Identify extraprostatic extension
- Check for seminal vesicle invasion
- Help with surgical planning

**Active surveillance:**
- Monitoring low-risk prostate cancer
- Detect progression or new lesions
- Guide repeat biopsies

**When NOT typically used:**
- Very low PSA with no risk factors
- Routine screening in asymptomatic men`,

      resultsInterpretation: `**PI-RADS 1-2 (Low suspicion):**
- No dominant lesion or only benign findings
- Clinically significant cancer is unlikely
- May avoid biopsy in appropriate clinical context
- Continue monitoring with PSA

**PI-RADS 3 (Equivocal):**
- Uncertain findings
- Decision to biopsy depends on other factors:
  - PSA level and trend
  - Family history
  - Patient preference
- May repeat MRI or proceed with biopsy

**PI-RADS 4 (High suspicion):**
- Lesion with features concerning for cancer
- Biopsy typically recommended
- MRI-targeted biopsy preferred

**PI-RADS 5 (Very high suspicion):**
- Lesion highly likely to be significant cancer
- Biopsy strongly recommended
- May show extraprostatic extension

**Additional findings reported:**
- Prostate volume (helps calculate PSA density)
- Benign conditions (BPH nodules, cysts, calcifications)
- Incidental findings in surrounding structures

**Limitations to understand:**
- MRI can miss some cancers (not 100% sensitive)
- Some cancers don't show on MRI
- False positives can occur (inflammation mimicking cancer)
- Quality depends on equipment and radiologist expertise`,

      nextSteps: `**If MRI is normal or low-risk (PI-RADS 1-2):**
- May avoid biopsy if PSA and clinical picture support this
- Continue PSA monitoring
- Repeat MRI if PSA rises significantly
- Shared decision-making with your urologist

**If MRI shows intermediate finding (PI-RADS 3):**
- Discuss biopsy vs. continued monitoring
- Consider PSA density and other risk factors
- If biopsy chosen, target the lesion
- Reasonable to repeat MRI in 6-12 months

**If MRI shows suspicious lesion (PI-RADS 4-5):**
- MRI-fusion targeted biopsy recommended
- Combines MRI images with real-time ultrasound
- More accurate than standard "blind" biopsy
- Usually combined with systematic sampling

**If cancer is confirmed:**
- MRI helps with staging and treatment planning
- Guides decisions about surgery vs. radiation
- Identifies candidates for active surveillance
- May need additional staging tests (bone scan, CT)

**After treatment:**
- MRI can monitor for recurrence
- Useful after radiation therapy
- Can guide salvage treatments if needed`,

      relatedConditions: [
        { title: 'Prostate Cancer (Screening & Localized Disease)', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { title: 'Understanding Active Surveillance for Prostate Cancer', articleId: '86764b75-bfe3-4065-9c7a-23f1c8d5dd25' },
        { title: 'Prostate Cancer Treatment Options', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' },
        { title: 'Prostatectomy vs. Radiation for Prostate Cancer', articleId: 'c6a9d661-62f5-448e-8d69-d477ad16d6da' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' }
      ]
    }
  },

  {
    id: 'ct-kidney-stones',
    title: 'CT Scan for Kidney Stones',
    icon: 'layers',
    category: 'Imaging',
    summary: 'A non-contrast CT scan that quickly and accurately detects kidney stones and determines their size and location.',
    content: {
      whatItMeasures: `A non-contrast CT scan (also called CT KUB or renal colic protocol CT) is the gold standard imaging test for kidney stones.

**What the scan shows:**
- **Stone presence** — detects nearly all stone types
- **Stone size** — measured in millimeters
- **Stone location** — kidney, ureter, or bladder
- **Number of stones** — may be multiple
- **Stone density** — measured in Hounsfield Units (HU)
- **Hydronephrosis** — kidney swelling from obstruction
- **Other findings** — other abdominal/pelvic abnormalities

**Why non-contrast CT is preferred:**
- No IV contrast needed (safer for kidneys)
- Very fast (completed in minutes)
- Detects >95% of stones
- Shows stone size and location precisely
- Can identify other causes of pain

**Stone characteristics assessed:**
| Feature | Clinical Relevance |
|---------|-------------------|
| Size | Predicts passage likelihood |
| Location | Guides treatment approach |
| Density (HU) | Predicts response to shock wave therapy |
| Skin-to-stone distance | Affects shock wave treatment planning |`,

      whyOrdered: `CT for kidney stones is ordered in these situations:

**Acute kidney stone symptoms:**
- Sudden, severe flank or back pain
- Pain radiating to groin
- Blood in urine with pain
- Nausea/vomiting with flank pain

**Confirming suspected stone:**
- After positive findings on ultrasound
- When X-ray is inconclusive
- Prior to planning treatment

**Treatment planning:**
- Determining if intervention is needed
- Choosing between treatment options
- Planning shock wave lithotripsy or ureteroscopy

**Follow-up imaging:**
- Confirming stone passage
- Checking for residual fragments after treatment
- Monitoring known stones

**Alternatives to CT:**
- **Ultrasound** — first choice in pregnancy, children, and for follow-up
- **X-ray (KUB)** — can see some stones, misses others
- **CT with contrast** — for complex cases or tumor evaluation`,

      resultsInterpretation: `**Stone size and passage likelihood:**

| Stone Size | Spontaneous Passage Rate |
|------------|-------------------------|
| <4 mm | ~80% will pass |
| 4-5 mm | ~60% will pass |
| 5-6 mm | ~40% will pass |
| 6-7 mm | ~20% will pass |
| >7 mm | Usually requires intervention |

**Stone location matters:**
- **Kidney stones** — may not cause symptoms unless moving
- **Upper ureter** — often painful, may need intervention
- **Mid ureter** — often gets stuck at crossing blood vessels
- **Lower ureter (near bladder)** — most likely to pass
- **UVJ (ureter-bladder junction)** — common location for obstruction

**Degree of obstruction:**
- **Mild hydronephrosis** — usually manageable
- **Moderate hydronephrosis** — may need intervention
- **Severe hydronephrosis** — often requires urgent treatment

**Stone density (Hounsfield Units):**
- **<500 HU** — softer stone, responds well to shock waves
- **500-1000 HU** — moderate density
- **>1000 HU** — hard stone, may need ureteroscopy/laser

**Other findings to note:**
- Multiple stones in kidney
- Stones in both kidneys
- Ureteral wall thickening (inflammation)
- Perinephric stranding (swelling around kidney)`,

      nextSteps: `**Small stone (<5mm) with mild symptoms:**
- Conservative management (watchful waiting)
- Pain control with NSAIDs and/or opioids
- Alpha-blocker medication (tamsulosin) to help passage
- Hydration
- Strain urine to catch the stone
- Follow-up in 2-4 weeks

**Medium stone (5-10mm) or moderate symptoms:**
- Trial of passage may be reasonable
- Consider intervention if not passing
- Options: shock wave lithotripsy or ureteroscopy
- Decision based on stone location and patient factors

**Large stone (>10mm) or severe symptoms:**
- Intervention usually needed
- Ureteroscopy with laser lithotripsy
- Percutaneous nephrolithotomy for very large stones
- Shock wave lithotripsy for some kidney stones

**Signs requiring urgent intervention:**
- Fever with obstructing stone (infected stone = emergency)
- Severe uncontrolled pain
- Kidney function declining
- Single kidney with obstruction
- Bilateral obstructing stones

**After stone passage or treatment:**
- Strain urine to collect stone for analysis
- Stone composition guides prevention
- 24-hour urine testing for recurrent stone formers
- Dietary and fluid recommendations
- Follow-up imaging to confirm clearance`,

      relatedConditions: [
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Dietary Recommendations for Kidney Stone Prevention', articleId: '659b8ce7-fc18-4ee9-94ec-c27516e8b325' },
        { title: 'Hydronephrosis: When the Kidney Swells', articleId: 'd7e6d442-cdee-4b79-a91c-40b103cb883a' },
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' },
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' }
      ]
    }
  },

  {
    id: 'semen-analysis',
    title: 'Semen Analysis',
    icon: 'microscope',
    category: 'Fertility Test',
    summary: 'A laboratory evaluation of semen that assesses sperm count, movement, and shape to evaluate male fertility.',
    content: {
      whatItMeasures: `Semen analysis (also called a spermogram) is the cornerstone test for evaluating male fertility. It measures multiple parameters of semen and sperm.

**Key parameters measured:**

**Volume & Appearance:**
- **Semen volume** — amount of ejaculate
- **pH** — acidity/alkalinity
- **Liquefaction time** — how long to become liquid
- **Color and consistency**

**Sperm Concentration:**
- **Sperm count** — millions of sperm per milliliter
- **Total sperm count** — total number in the sample

**Sperm Motility (Movement):**
- **Total motility** — % of sperm moving
- **Progressive motility** — % moving forward effectively
- **Non-progressive motility** — moving but not forward

**Sperm Morphology (Shape):**
- **Normal forms** — % with normal head, midpiece, and tail
- **Abnormal forms** — head defects, midpiece defects, tail defects

**Other components:**
- White blood cells (may indicate infection)
- Immature germ cells
- Agglutination (clumping)`,

      whyOrdered: `Semen analysis is ordered in several situations:

**Infertility evaluation:**
- Couple trying to conceive for 12+ months without success
- Partner over 35 trying for 6+ months
- Known risk factors for male infertility

**After vasectomy:**
- Confirm successful vasectomy (no sperm present)
- Typically checked at 8-12 weeks post-procedure
- Must show azoospermia (zero sperm)

**Before fertility treatments:**
- Prior to intrauterine insemination (IUI)
- Before in vitro fertilization (IVF)
- Assessing need for intracytoplasmic sperm injection (ICSI)

**Evaluating specific conditions:**
- After varicocele repair
- Following treatment for infection
- Monitoring response to hormonal therapy

**Sperm banking:**
- Before cancer treatment (chemotherapy, radiation)
- Before vasectomy as backup
- For fertility preservation`,

      resultsInterpretation: `**WHO Reference Values (2021):**

| Parameter | Normal Range |
|-----------|--------------|
| Volume | ≥1.4 mL |
| Sperm concentration | ≥16 million/mL |
| Total sperm count | ≥39 million per ejaculate |
| Total motility | ≥42% |
| Progressive motility | ≥30% |
| Normal morphology | ≥4% normal forms |
| Vitality | ≥54% live sperm |
| pH | 7.2-8.0 |

**Common abnormal findings:**

| Term | Definition |
|------|------------|
| **Oligozoospermia** | Low sperm count (<16 million/mL) |
| **Asthenozoospermia** | Poor sperm motility (<42% motile) |
| **Teratozoospermia** | Abnormal sperm shape (<4% normal) |
| **Azoospermia** | No sperm in ejaculate |
| **Oligoasthenoteratozoospermia (OAT)** | All three abnormalities combined |
| **Hypospermia** | Low semen volume (<1.4 mL) |

**Important considerations:**
- One abnormal result should be confirmed with repeat testing
- Sperm parameters naturally fluctuate
- Recent illness, fever, or stress can temporarily affect results
- Abstinence period affects results (2-5 days recommended)
- Results should be interpreted by a specialist`,

      nextSteps: `**If semen analysis is normal:**
- Male factor is less likely the cause of infertility
- Partner evaluation important
- May still benefit from lifestyle optimization
- Repeat testing not usually needed

**If mild abnormalities:**
- Repeat semen analysis in 2-3 months
- Lifestyle modifications:
  - Avoid excessive heat (hot tubs, saunas)
  - Limit alcohol and tobacco
  - Maintain healthy weight
  - Reduce stress
- Antioxidant supplements may help

**If significant abnormalities:**
- Further evaluation by urologist or reproductive specialist
- Additional testing may include:
  - Hormone levels (FSH, LH, testosterone)
  - Scrotal ultrasound
  - Genetic testing (for severe oligospermia or azoospermia)
  - Post-ejaculatory urinalysis (if low volume)

**If azoospermia (no sperm):**
- Critical to determine cause:
  - **Obstructive** — blockage in reproductive tract
  - **Non-obstructive** — production problem
- Hormone testing
- Genetic testing (Y-chromosome microdeletion, karyotype)
- May need testicular biopsy or sperm extraction

**Treatment options based on results:**
- IUI (intrauterine insemination) — for mild abnormalities
- IVF (in vitro fertilization) — for moderate abnormalities
- ICSI (intracytoplasmic sperm injection) — for severe abnormalities
- Surgical sperm retrieval — for azoospermia
- Varicocele repair — if varicocele present
- Hormonal therapy — if hormone imbalance`,

      relatedConditions: [
        { title: 'Understanding Your Semen Analysis', articleId: 'f7df9ddd-3594-423a-be24-079c97ffdcc9' },
        { title: 'Male Infertility: Causes, Evaluation & Treatment', articleId: 'b966fdc4-6310-48da-ad3c-fca8c711c555' },
        { title: 'Supplements for Male Infertility', articleId: '11e4d5cd-ce3a-4a03-b582-7e808fd23758' },
        { title: 'Varicocele: Diagnosis, Treatment & Fertility Impact', articleId: '629a6dd6-1569-4756-8783-6dbd509642dc' },
        { title: 'Low Testosterone (Hypogonadism)', articleId: '9fb844eb-d246-4f58-9e07-366d5d175343' }
      ]
    }
  },

  {
    id: 'cystoscopy',
    title: 'Cystoscopy',
    icon: 'eye',
    category: 'Procedure',
    summary: 'A procedure using a small camera to look inside the bladder and urethra to diagnose and sometimes treat urinary tract problems.',
    content: {
      whatItMeasures: `Cystoscopy is a procedure that allows your doctor to directly visualize the inside of your urethra and bladder using a cystoscope — a thin tube with a camera and light.

**What the procedure examines:**
- **Urethra** — the tube that carries urine from the bladder
- **Bladder lining** — looking for tumors, inflammation, or abnormalities
- **Prostate** (in men) — the area where the urethra passes through
- **Ureteral openings** — where ureters enter the bladder

**Types of cystoscopy:**

| Type | Description | Setting |
|------|-------------|---------|
| Flexible cystoscopy | Uses bendable scope, more comfortable | Usually office |
| Rigid cystoscopy | Provides better visualization, allows procedures | Operating room |
| Fluorescence cystoscopy | Uses blue light to detect tumors | Operating room |

**What can be done during cystoscopy:**
- Visual inspection and photography
- Biopsy of suspicious areas
- Removal of small bladder tumors
- Placement or removal of ureteral stents
- Injection of Botox into bladder
- Stone removal or fragmentation`,

      whyOrdered: `Cystoscopy is ordered to investigate and treat various conditions:

**Diagnostic purposes:**
- **Hematuria (blood in urine)** — to find the source
- **Recurrent UTIs** — looking for anatomical causes
- **Bladder cancer surveillance** — monitoring for recurrence
- **Unexplained urinary symptoms** — frequency, urgency, pain
- **Abnormal imaging findings** — confirming suspected abnormalities

**Therapeutic purposes:**
- Removing or biopsying bladder tumors
- Treating urethral strictures
- Placing or removing stents
- Injecting medications (Botox for OAB)
- Removing foreign bodies or stones

**Pre-operative planning:**
- Evaluating anatomy before surgery
- Assessing prostate size and bladder neck
- Planning for procedures like TURP

**NOT typically needed for:**
- Uncomplicated UTI in young women
- Routine screening without symptoms
- When non-invasive imaging is sufficient`,

      resultsInterpretation: `**Normal findings:**
- Smooth, pink bladder lining (mucosa)
- No masses, tumors, or lesions
- Normal ureteral openings (orifices)
- No stones or foreign bodies
- Normal-appearing urethra
- In men: normal prostatic urethra without significant obstruction

**Abnormal findings and their significance:**

| Finding | Possible Meaning |
|---------|------------------|
| Bladder tumor | May be cancerous; needs biopsy |
| Inflammation (cystitis) | Infection, radiation effect, or interstitial cystitis |
| Trabeculation | Bladder wall thickening from obstruction (like BPH) |
| Diverticula | Outpouchings in bladder wall |
| Stones | Bladder calculi requiring removal |
| Urethral stricture | Scar tissue narrowing the urethra |
| Prostatic enlargement | BPH causing obstruction |
| Red patches (erythema) | Inflammation or carcinoma in situ |
| Papillary lesions | Often bladder cancer; needs biopsy |

**Biopsy results (if taken):**
- Typically available within 1 week
- Pathology report will classify any tumor
- Results guide further treatment decisions`,

      nextSteps: `**After a normal cystoscopy:**
- Reassurance that bladder and urethra appear healthy
- May need other tests if symptoms persist
- Follow-up depends on original indication

**After finding bladder tumor:**
- Biopsy results determine cancer type and grade
- If cancer confirmed:
  - Small tumors may be removed during cystoscopy
  - Larger tumors need TURBT (transurethral resection)
  - Staging determines further treatment
  - Surveillance cystoscopy schedule established

**After finding stricture:**
- May be treated during same procedure
- Options: dilation or internal urethrotomy
- May need urethroplasty for complex strictures
- Follow-up to monitor for recurrence

**After finding inflammation:**
- Further testing for cause (culture, cytology)
- Treatment based on underlying condition
- May need repeat cystoscopy after treatment

**Post-procedure expectations:**
- Mild burning with urination for 24-48 hours
- Pink-tinged urine is normal briefly
- Drink plenty of fluids
- Avoid strenuous activity for 24 hours

**Call your doctor if:**
- Fever over 101°F
- Inability to urinate
- Significant blood clots
- Severe pain not relieved by medication`,

      relatedConditions: [
        { title: 'Bladder Cancer (Non-Muscle Invasive)', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' },
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' },
        { title: 'Urethral Stricture', articleId: 'bd398644-5720-4c8b-8ea2-2265ea628c01' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'Overactive Bladder (OAB)', articleId: '4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6' },
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' }
      ]
    }
  },

  {
    id: 'urodynamic-studies',
    title: 'Urodynamic Studies',
    icon: 'activity',
    category: 'Procedure',
    summary: 'A series of tests that measure how well the bladder, sphincters, and urethra store and release urine.',
    content: {
      whatItMeasures: `Urodynamic studies (UDS) are a group of tests that evaluate how your lower urinary tract functions. They measure pressures, volumes, and flow to understand bladder behavior.

**Components of urodynamic testing:**

**Uroflowmetry:**
- Measures urine flow rate
- You urinate into a special toilet
- Records volume and flow pattern
- Non-invasive, usually done first

**Post-Void Residual (PVR):**
- Measures urine left in bladder after voiding
- Done with ultrasound or catheter
- Indicates completeness of emptying

**Cystometry (CMG):**
- Measures bladder pressure during filling
- Small catheter placed in bladder
- Saline slowly fills the bladder
- Records when you feel urges and capacity

**Pressure-Flow Study:**
- Measures bladder pressure during urination
- Identifies obstruction vs. weak bladder
- Combined with uroflowmetry

**Electromyography (EMG):**
- Measures pelvic floor muscle activity
- Sensors placed near anal sphincter
- Shows coordination during voiding

**Video Urodynamics:**
- Combines pressure measurements with X-ray imaging
- Provides anatomical and functional information
- Most comprehensive urodynamic test`,

      whyOrdered: `Urodynamic studies help diagnose complex bladder problems:

**When symptoms are unclear:**
- Mixed urinary symptoms (urgency + weak stream)
- Treatment hasn't worked as expected
- Need to distinguish between conditions

**Before certain surgeries:**
- Prior to incontinence surgery
- Before BPH surgery (to confirm obstruction)
- Planning complex reconstructive procedures

**Specific conditions:**
- **Overactive bladder** — to confirm involuntary contractions
- **Urinary retention** — to determine if obstruction or weak bladder
- **Incontinence** — to classify type (stress vs. urge vs. mixed)
- **Neurogenic bladder** — spinal cord injury, MS, diabetes complications
- **Failed prior treatments** — understanding why medications didn't work

**After certain events:**
- Spinal cord injury
- Pelvic surgery with new symptoms
- Radiation to pelvis

**NOT typically needed for:**
- Simple, uncomplicated overactive bladder
- Clear-cut stress incontinence
- Straightforward BPH responding to medication`,

      resultsInterpretation: `**Key measurements and normal values:**

| Parameter | Normal Range | Significance |
|-----------|--------------|--------------|
| Max flow rate (Qmax) | >15 mL/sec (men), >20 mL/sec (women) | Lower suggests obstruction |
| Post-void residual | <50-100 mL | Higher suggests incomplete emptying |
| Bladder capacity | 400-600 mL | Lower in OAB, higher in retention |
| First sensation | 150-250 mL | Earlier in OAB |
| Detrusor pressure at Qmax | <40 cmH2O (men) | Higher suggests obstruction |

**Common findings and diagnoses:**

**Detrusor overactivity (DO):**
- Involuntary bladder contractions during filling
- Correlates with overactive bladder symptoms
- May cause urgency and urge incontinence

**Bladder outlet obstruction (BOO):**
- High pressure needed to urinate
- Low flow rate despite good bladder contraction
- Common in men with BPH

**Detrusor underactivity:**
- Weak or absent bladder contraction
- Results in poor emptying
- May need intermittent catheterization

**Stress urinary incontinence:**
- Leakage with increased abdominal pressure
- Cough or strain causes leakage
- No involuntary bladder contraction

**Mixed incontinence:**
- Both stress and urge components
- Guides treatment prioritization

**Neurogenic findings:**
- Detrusor-sphincter dyssynergia
- Poor compliance (stiff bladder)
- May indicate need for protective treatment`,

      nextSteps: `**Based on detrusor overactivity:**
- Confirms diagnosis of overactive bladder
- Supports use of medications (antimuscarinics, beta-3 agonists)
- May be candidate for Botox or neuromodulation
- Guides realistic expectations for treatment

**Based on bladder outlet obstruction:**
- In men: confirms need for BPH treatment
- Supports surgical intervention if medical therapy fails
- Helps choose appropriate procedure
- Predicts outcome of surgery

**Based on detrusor underactivity:**
- May explain retention despite no obstruction
- Clean intermittent catheterization often recommended
- Surgery less likely to help
- Focus on bladder emptying strategies

**Based on stress incontinence:**
- Confirms diagnosis for surgical planning
- Sling or other procedures may be recommended
- Physical therapy may be first-line option

**Based on neurogenic findings:**
- May need protective measures for kidneys
- Regular monitoring of upper tracts
- Catheterization program if needed
- Possible need for Botox or augmentation

**Follow-up after urodynamics:**
- Results discussed with your urologist
- Treatment plan developed based on findings
- May repeat testing after treatment to assess response
- Some patients benefit from repeat studies over time`,

      relatedConditions: [
        { title: 'Overactive Bladder (OAB)', articleId: '4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'Urinary Retention', articleId: '3389df5b-d448-4779-8d46-870a74cf8671' },
        { title: 'Urinary Incontinence', articleId: '2c669205-f1b7-4961-966a-22e1db4f72f8' },
        { title: 'Stress Urinary Incontinence', articleId: '827985a9-5f5b-4a35-972a-3d4503bfa5d0' },
        { title: 'Urge Urinary Incontinence', articleId: 'b284ebbb-c55c-4f6f-8c9a-bc7496325d2f' }
      ]
    }
  },

  {
    id: 'scrotal-ultrasound',
    title: 'Scrotal Ultrasound',
    icon: 'scan',
    category: 'Imaging',
    summary: 'An imaging test using sound waves to examine the testicles, epididymis, and surrounding structures.',
    content: {
      whatItMeasures: `Scrotal ultrasound uses high-frequency sound waves to create detailed images of the structures inside the scrotum. It's non-invasive and uses no radiation.

**Structures examined:**
- **Testicles** — size, shape, texture, blood flow
- **Epididymis** — the coiled tube behind each testicle
- **Spermatic cord** — blood vessels and vas deferens
- **Scrotal wall** — skin and surrounding tissues
- **Fluid collections** — hydroceles, cysts

**What the ultrasound shows:**

| Structure | Normal Appearance |
|-----------|-------------------|
| Testicle | Homogeneous (uniform) echogenicity, ~4-5 cm length |
| Epididymis | Small, slightly less echogenic than testicle |
| Blood flow | Symmetric flow to both testicles on Doppler |
| Fluid | Minimal amount is normal |

**Doppler ultrasound component:**
- Evaluates blood flow to testicles
- Critical for diagnosing testicular torsion
- Can identify varicoceles
- Assesses tumor vascularity

**Advantages of scrotal ultrasound:**
- No radiation exposure
- Non-invasive and painless
- Real-time imaging
- Can be done quickly
- Excellent for soft tissue detail`,

      whyOrdered: `Scrotal ultrasound is ordered for many reasons:

**Acute conditions (urgent):**
- **Testicular torsion** — sudden severe pain (emergency)
- **Trauma** — injury to scrotum
- **Acute epididymitis/orchitis** — infection with pain and swelling

**Evaluating masses or lumps:**
- Palpable lump felt by patient or doctor
- Distinguishing solid mass from fluid (cyst)
- Characterizing testicular tumors
- Evaluating abnormal physical exam

**Pain evaluation:**
- Chronic testicular pain
- Scrotal discomfort
- Pain with unclear cause

**Swelling evaluation:**
- Hydrocele (fluid around testicle)
- Varicocele (enlarged veins)
- Epididymal cyst (spermatocele)
- Inguinal hernia extending into scrotum

**Infertility workup:**
- Looking for varicocele
- Evaluating testicular size
- Assessing for obstruction

**Follow-up:**
- Monitoring known conditions
- Post-treatment evaluation
- Surveillance after testicular cancer treatment`,

      resultsInterpretation: `**Normal findings:**
- Testicles symmetric in size (within 20%)
- Homogeneous echotexture
- Equal blood flow on Doppler
- Epididymis normal in size and appearance
- No masses or collections

**Abnormal findings:**

| Finding | Significance |
|---------|--------------|
| **Solid testicular mass** | High concern for cancer; needs urologist referral |
| **No blood flow to testicle** | Testicular torsion (emergency surgery) |
| **Increased blood flow to epididymis** | Epididymitis (infection) |
| **Dilated veins (>3mm)** | Varicocele |
| **Fluid around testicle** | Hydrocele |
| **Cyst in epididymis** | Spermatocele (usually benign) |
| **Heterogeneous testicle** | May indicate tumor, infection, or infarction |
| **Microlithiasis** | Tiny calcifications; may need monitoring |
| **Absent testicle** | Undescended testicle or prior removal |

**Testicular masses — key points:**
- Any solid mass in testicle is cancer until proven otherwise
- Ultrasound cannot definitively diagnose cancer type
- Solid masses typically require surgery (orchiectomy)
- Cysts and fluid collections are usually benign

**Varicocele grading:**
| Grade | Description |
|-------|-------------|
| Grade 1 | Veins >3mm with Valsalva only |
| Grade 2 | Veins >3mm at rest, increase with Valsalva |
| Grade 3 | Large, easily visible dilated veins |`,

      nextSteps: `**If ultrasound is normal:**
- Reassurance for patients with palpable concerns
- May still need clinical follow-up for symptoms
- Repeat imaging if symptoms persist or change

**If testicular mass is found:**
- **Urgent urology referral**
- Blood tests: tumor markers (AFP, beta-hCG, LDH)
- CT scan of abdomen/pelvis/chest for staging
- Surgery (radical orchiectomy) is typically needed
- Do NOT biopsy testicular masses (risk of spread)

**If torsion is suspected:**
- **Surgical emergency** — time-sensitive
- Surgery within 6 hours gives best chance to save testicle
- Ultrasound may be skipped if clinical suspicion high
- Both testicles are fixed during surgery (orchiopexy)

**If epididymitis is diagnosed:**
- Antibiotics prescribed
- Pain management
- Scrotal support and rest
- Follow-up to ensure resolution
- If not improving, repeat ultrasound

**If varicocele is found:**
- Most don't require treatment
- Consider repair if:
  - Causing pain
  - Affecting fertility (with abnormal semen analysis)
  - Testicle size discrepancy in adolescents
- Options: surgery or embolization

**If hydrocele/spermatocele is found:**
- Usually benign, often no treatment needed
- Surgery if large, bothersome, or growing
- Aspiration is temporary (fluid returns)

**Surveillance recommendations:**
- Testicular microlithiasis: annual self-exam, consider periodic ultrasound
- After testicular cancer: regular ultrasound of remaining testicle`,

      relatedConditions: [
        { title: 'Hydrocele and Spermatocele', articleId: '51f67acb-90a4-44f8-9d47-48c6f9f5dd3b' },
        { title: 'Varicocele: Diagnosis, Treatment & Fertility Impact', articleId: '629a6dd6-1569-4756-8783-6dbd509642dc' },
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { title: 'Male Infertility: Causes, Evaluation & Treatment', articleId: 'b966fdc4-6310-48da-ad3c-fca8c711c555' },
        { title: 'Understanding Your Semen Analysis', articleId: 'f7df9ddd-3594-423a-be24-079c97ffdcc9' }
      ]
    }
  },

  {
    id: 'testosterone-blood-test',
    title: 'Testosterone Blood Test',
    icon: 'droplet',
    category: 'Blood Test',
    summary: 'A blood test that measures testosterone levels to evaluate hormonal health and symptoms of low testosterone.',
    content: {
      whatItMeasures: `The testosterone blood test measures the level of testosterone hormone in your blood. Testosterone is the primary male sex hormone, essential for many body functions.

**Types of testosterone measured:**

| Type | Description |
|------|-------------|
| **Total testosterone** | All testosterone in blood (bound + free) |
| **Free testosterone** | Testosterone not bound to proteins (~2-3% of total) |
| **Bioavailable testosterone** | Free + loosely bound testosterone |
| **SHBG** | Sex hormone-binding globulin (protein that binds testosterone) |

**Key points about testing:**
- **Morning draw is essential** — testosterone peaks in early morning
- Should be drawn between 7-10 AM
- Two separate low readings needed for diagnosis
- Levels naturally decline with age (~1-2% per year after 30)

**Other hormones often tested together:**
- **LH (luteinizing hormone)** — stimulates testosterone production
- **FSH (follicle-stimulating hormone)** — involved in sperm production
- **Prolactin** — elevated levels can lower testosterone
- **Estradiol** — testosterone can convert to estrogen`,

      whyOrdered: `Testosterone testing is ordered to evaluate:

**Symptoms of low testosterone:**
- Decreased libido (sex drive)
- Erectile dysfunction
- Fatigue and low energy
- Depressed mood
- Loss of muscle mass
- Increased body fat
- Decreased bone density

**Infertility evaluation:**
- Part of male fertility workup
- Often combined with semen analysis
- Assessing hormonal causes of low sperm count

**Monitoring testosterone therapy:**
- Baseline before starting treatment
- Regular monitoring during TRT
- Adjusting dosage based on levels

**Other indications:**
- Delayed puberty in adolescents
- Suspected pituitary problems
- Evaluating testicular function after injury or infection
- Assessing androgen excess in women

**When NOT to test:**
- In men without symptoms (routine screening not recommended)
- Single test in afternoon (will be falsely low)
- During acute illness (temporarily lowers testosterone)`,

      resultsInterpretation: `**Normal testosterone ranges:**

| Age | Total Testosterone |
|-----|-------------------|
| Adult men | 300-1000 ng/dL |
| Men >65 | Lower end often acceptable |

**Note:** Ranges vary between laboratories. Always compare to your lab's reference range.

**Interpreting results:**

| Total Testosterone | Interpretation |
|-------------------|----------------|
| >300 ng/dL | Generally normal |
| 200-300 ng/dL | Borderline low (gray zone) |
| <200 ng/dL | Clearly low |

**If testosterone is low, LH helps determine cause:**

| Pattern | Likely Cause |
|---------|--------------|
| Low testosterone + High LH | Primary hypogonadism (testicular problem) |
| Low testosterone + Low/Normal LH | Secondary hypogonadism (pituitary/hypothalamus) |

**Causes of low testosterone:**
- **Primary:** Testicular failure, Klinefelter syndrome, injury, infection, radiation
- **Secondary:** Pituitary tumors, medications (opioids, steroids), obesity, chronic illness

**Factors affecting results:**
- Time of day (highest in morning)
- Sleep quality
- Acute illness
- Obesity (lowers testosterone)
- Medications (opioids, corticosteroids)
- Diabetes and metabolic syndrome`,

      nextSteps: `**If testosterone is normal:**
- Low testosterone not the cause of symptoms
- Evaluate other causes (depression, thyroid, sleep apnea)
- No testosterone treatment indicated

**If testosterone is borderline (200-300 ng/dL):**
- Repeat test on different day (morning)
- Consider free testosterone level
- Evaluate symptoms carefully
- Lifestyle modifications may help:
  - Weight loss
  - Exercise
  - Better sleep
  - Reduce alcohol

**If testosterone is clearly low (<200-300 ng/dL):**
- Confirm with second morning test
- Additional workup:
  - LH and FSH (determine cause)
  - Prolactin (rule out pituitary issue)
  - MRI of pituitary if secondary hypogonadism
- Discuss testosterone replacement therapy (TRT) if symptomatic
- Address reversible causes first (obesity, medications, sleep apnea)

**Starting testosterone therapy:**
- Review risks and benefits
- Discuss fertility implications (TRT suppresses sperm production)
- Baseline labs: PSA, hematocrit, lipids
- Choose delivery method (injections, gels, patches)
- Regular monitoring schedule

**Monitoring on TRT:**
- Testosterone level (target: mid-normal range)
- Hematocrit (can increase with TRT)
- PSA (monitor prostate)
- Symptom assessment
- Typically every 3-6 months initially`,

      relatedConditions: [
        { title: 'Low Testosterone (Hypogonadism)', articleId: '9fb844eb-d246-4f58-9e07-366d5d175343' },
        { title: 'Testosterone Replacement Therapy', articleId: 'b598e8ea-f7c0-4fbc-bf60-86c56a9150dc' },
        { title: 'Erectile Dysfunction: Complete Guide', articleId: 'cf6fe53f-c9a4-4966-b84e-8552d936dcb6' },
        { title: 'Male Infertility: Causes, Evaluation & Treatment', articleId: 'b966fdc4-6310-48da-ad3c-fca8c711c555' },
        { title: 'Understanding Your Semen Analysis', articleId: 'f7df9ddd-3594-423a-be24-079c97ffdcc9' }
      ]
    }
  },

  // ============================================
  // NEW TESTS - BATCH 2
  // ============================================

  {
    id: 'kidney-function-panel',
    title: 'Kidney Function Panel (BMP/CMP)',
    icon: 'droplet',
    category: 'Blood Test',
    summary: 'Blood tests that measure how well your kidneys are filtering waste and maintaining proper electrolyte balance.',
    content: {
      whatItMeasures: `The kidney function panel measures several markers in your blood that indicate how well your kidneys are working.

**Key measurements:**

| Test | What It Measures | Normal Range |
|------|------------------|--------------|
| **Creatinine** | Waste product from muscle metabolism | 0.7-1.3 mg/dL (men), 0.6-1.1 mg/dL (women) |
| **BUN** (Blood Urea Nitrogen) | Waste from protein breakdown | 7-20 mg/dL |
| **GFR** (Glomerular Filtration Rate) | Overall kidney filtering capacity | >90 mL/min/1.73m2 |`,

      whyOrdered: `Kidney function tests are ordered in many situations:

**Routine monitoring:**
- Annual physical examination
- Patients with diabetes or hypertension
- Patients on medications that affect kidneys

**Urologic indications:**
- Kidney stones
- Hydronephrosis
- Urinary retention
- Before contrast imaging`,

      resultsInterpretation: `**Normal results:**
- Creatinine within normal range
- GFR >90 mL/min
- BUN within normal range

**Abnormal findings:**
- Elevated creatinine/Low GFR indicates kidney impairment
- May be acute (reversible) or chronic`,

      nextSteps: `**If normal:** Continue routine monitoring
**If mildly abnormal:** Repeat testing, review medications
**If moderately abnormal:** Nephrology referral may be needed
**If severely abnormal:** Urgent evaluation required`,

      relatedConditions: [
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Hydronephrosis: When the Kidney Swells', articleId: 'd7e6d442-cdee-4b79-a91c-40b103cb883a' }
      ]
    }
  },

  {
    id: 'complete-blood-count',
    title: 'Complete Blood Count (CBC)',
    icon: 'droplet',
    category: 'Blood Test',
    summary: 'A common blood test measuring red cells, white cells, and platelets to evaluate overall health.',
    content: {
      whatItMeasures: `The CBC measures cellular components of blood including red blood cells (oxygen carrying), white blood cells (infection fighting), and platelets (clotting).`,

      whyOrdered: `Ordered for pre-operative evaluation, hematuria workup, infection evaluation, and monitoring testosterone therapy effects.`,

      resultsInterpretation: `**Normal:** Hemoglobin 13.5-17.5 g/dL (men), WBC 4,500-11,000/uL, Platelets 150,000-400,000/uL
**Anemia:** Low hemoglobin - evaluate cause
**Elevated WBC:** Suggests infection`,

      nextSteps: `Treatment depends on specific abnormality found. Anemia may need iron studies; elevated WBC needs infection workup.`,

      relatedConditions: [
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' },
        { title: 'Testosterone Replacement Therapy', articleId: 'b598e8ea-f7c0-4fbc-bf60-86c56a9150dc' }
      ]
    }
  },

  {
    id: '24-hour-urine',
    title: '24-Hour Urine Collection',
    icon: 'flask',
    category: 'Urine Test',
    summary: 'Collects all urine over 24 hours to identify causes of kidney stones and guide prevention.',
    content: {
      whatItMeasures: `Measures calcium, oxalate, citrate, uric acid, sodium, and volume to identify metabolic abnormalities causing kidney stones.`,

      whyOrdered: `For kidney stone prevention workup, especially in recurrent stone formers or after first stone episode.`,

      resultsInterpretation: `**High calcium:** Calcium stone risk - limit sodium, consider thiazide
**High oxalate:** Limit high-oxalate foods
**Low citrate:** Add potassium citrate
**High uric acid:** Reduce purines, consider allopurinol`,

      nextSteps: `Dietary modifications and/or medications based on specific abnormalities. Repeat testing in 3-6 months to confirm improvement.`,

      relatedConditions: [
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Dietary Recommendations for Kidney Stone Prevention', articleId: '659b8ce7-fc18-4ee9-94ec-c27516e8b325' }
      ]
    }
  },

  {
    id: 'urine-cytology',
    title: 'Urine Cytology',
    icon: 'microscope',
    category: 'Urine Test',
    summary: 'Microscopic examination of urine cells to detect bladder cancer and other urinary tract cancers.',
    content: {
      whatItMeasures: `Pathologist examines cells shed into urine looking for malignant or pre-malignant changes. Best at detecting high-grade bladder cancer.`,

      whyOrdered: `Part of hematuria workup, bladder cancer surveillance, and when symptoms suggest possible cancer.`,

      resultsInterpretation: `**Negative:** No malignant cells (doesn't rule out low-grade cancer)
**Atypical:** Abnormal cells, not diagnostic - needs clinical correlation
**Suspicious/Positive:** High concern for cancer - urgent cystoscopy needed`,

      nextSteps: `Positive or suspicious cytology requires thorough cystoscopy and upper tract evaluation. Negative cytology does not replace cystoscopy.`,

      relatedConditions: [
        { title: 'Bladder Cancer (Non-Muscle Invasive)', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' },
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' }
      ]
    }
  },

  {
    id: 'urine-pcr',
    title: 'Urine PCR Testing',
    icon: 'test-tube',
    category: 'Urine Test',
    summary: 'Molecular tests detecting genetic material from bacteria or viruses with high sensitivity for STI diagnosis.',
    content: {
      whatItMeasures: `PCR amplifies genetic material to detect Chlamydia, Gonorrhea, Mycoplasma, Ureaplasma, Trichomonas, and other organisms even when culture is negative.`,

      whyOrdered: `STI screening, urethritis evaluation, prostatitis workup, epididymitis diagnosis, and when standard cultures are negative.`,

      resultsInterpretation: `**Negative:** Organism not detected
**Positive Chlamydia/Gonorrhea:** Treat patient AND partners
**Positive Mycoplasma:** May need extended treatment`,

      nextSteps: `Appropriate antibiotic treatment, partner notification, test for other STIs, and test of cure for certain infections.`,

      relatedConditions: [
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' }
      ]
    }
  },

  {
    id: 'urine-fish',
    title: 'Urine FISH Test (UroVysion)',
    icon: 'microscope',
    category: 'Urine Test',
    summary: 'Molecular test detecting chromosomal abnormalities to help diagnose and monitor bladder cancer.',
    content: {
      whatItMeasures: `FISH detects extra copies of chromosomes 3, 7, 17 and deletion of 9p21 - abnormalities associated with bladder cancer. More sensitive than cytology for low-grade tumors.`,

      whyOrdered: `Hematuria evaluation adjunct, bladder cancer surveillance, atypical cytology follow-up, and when cystoscopy findings are equivocal.`,

      resultsInterpretation: `**Negative:** Low cancer likelihood
**Positive:** High concern for malignancy - may detect cancer before visible tumor
**Anticipatory positive:** Positive FISH with normal cystoscopy suggests cancer may develop`,

      nextSteps: `Positive FISH requires thorough cystoscopy, possible random biopsies, and upper tract evaluation. Close surveillance for anticipatory positive.`,

      relatedConditions: [
        { title: 'Bladder Cancer (Non-Muscle Invasive)', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' },
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' }
      ]
    }
  },

  {
    id: 'renal-ultrasound',
    title: 'Renal (Kidney) Ultrasound',
    icon: 'scan',
    category: 'Imaging',
    summary: 'Non-invasive imaging using sound waves to visualize kidneys and detect stones, cysts, and obstruction.',
    content: {
      whatItMeasures: `Evaluates kidney size, shape, hydronephrosis (swelling), stones, cysts, and masses. No radiation, safe in pregnancy.`,

      whyOrdered: `First-line for kidney stone evaluation in pregnancy/children, hydronephrosis assessment, kidney mass evaluation, and flank pain workup.`,

      resultsInterpretation: `**Normal:** Kidneys 9-12 cm, no hydronephrosis, no masses
**Hydronephrosis:** Indicates obstruction
**Simple cyst:** Benign
**Solid mass:** Needs CT for further evaluation`,

      nextSteps: `Management depends on findings. Hydronephrosis needs cause identified. Solid masses need CT with contrast for characterization.`,

      relatedConditions: [
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Hydronephrosis: When the Kidney Swells', articleId: 'd7e6d442-cdee-4b79-a91c-40b103cb883a' },
        { title: 'Renal Cysts and Small Renal Masses', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' }
      ]
    }
  },

  {
    id: 'ct-urogram',
    title: 'CT Urogram',
    icon: 'layers',
    category: 'Imaging',
    summary: 'Comprehensive CT scan with contrast visualizing the entire urinary tract for complete evaluation.',
    content: {
      whatItMeasures: `Multi-phase CT imaging kidneys, ureters, and bladder with and without contrast. Gold standard for hematuria workup and urothelial cancer detection.`,

      whyOrdered: `Complete hematuria workup, upper tract cancer evaluation, ureteral problems, kidney mass characterization, and complex stone planning.`,

      resultsInterpretation: `**Normal:** No masses, filling defects, or stones
**Filling defect in ureter:** Possible tumor or stone
**Kidney mass:** Characterize as cyst vs solid
**Ureteral thickening:** May indicate cancer or inflammation`,

      nextSteps: `Suspicious findings need ureteroscopy or biopsy. Kidney masses may need surgery. Normal CT urogram is reassuring but cystoscopy still needed for complete hematuria workup.`,

      relatedConditions: [
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' },
        { title: 'Bladder Cancer (Non-Muscle Invasive)', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' },
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' }
      ]
    }
  },

  {
    id: 'bone-scan',
    title: 'Bone Scan',
    icon: 'scan',
    category: 'Imaging',
    summary: 'Nuclear medicine test detecting areas of abnormal bone activity to check for cancer spread.',
    content: {
      whatItMeasures: `Radioactive tracer shows areas of increased bone metabolism indicating metastases, fractures, or infection. Whole skeleton imaged in one study.`,

      whyOrdered: `Prostate cancer staging (high-risk), bladder cancer staging, bone pain in cancer patients, and monitoring known metastases.`,

      resultsInterpretation: `**Negative:** No bone metastases
**Single lesion:** May be metastasis or benign - needs correlation
**Multiple lesions:** High likelihood of metastatic disease`,

      nextSteps: `Negative scan allows consideration of curative treatment. Positive scan changes staging and treatment approach to systemic therapy.`,

      relatedConditions: [
        { title: 'Prostate Cancer (Screening & Localized Disease)', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { title: 'Prostate Cancer Treatment Options', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' }
      ]
    }
  },

  {
    id: 'psma-pet-scan',
    title: 'PSMA PET Scan',
    icon: 'scan',
    category: 'Imaging',
    summary: 'Advanced imaging specifically targeting prostate cancer cells with high sensitivity throughout the body.',
    content: {
      whatItMeasures: `Radioactive tracer binds to PSMA protein on prostate cancer cells. Detects metastases with 85-95% sensitivity - far superior to conventional imaging.`,

      whyOrdered: `High-risk prostate cancer staging, biochemical recurrence localization, radiation planning, and selecting patients for PSMA therapy.`,

      resultsInterpretation: `**Negative:** Low likelihood of metastases - curative treatment appropriate
**Positive (limited disease):** May still benefit from aggressive local + systemic treatment
**Positive (extensive):** Systemic therapy primary approach`,

      nextSteps: `Changes management in 20-60% of cases. Guides treatment selection between local therapy, systemic therapy, or combination. Increasingly standard of care.`,

      relatedConditions: [
        { title: 'Prostate Cancer (Screening & Localized Disease)', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { title: 'Prostate Cancer Treatment Options', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' },
        { title: 'Understanding Active Surveillance for Prostate Cancer', articleId: '86764b75-bfe3-4065-9c7a-23f1c8d5dd25' }
      ]
    }
  },

  {
    id: 'prostate-biopsy',
    title: 'Prostate Biopsy',
    icon: 'crosshair',
    category: 'Procedure',
    summary: 'Procedure obtaining tissue samples from the prostate to diagnose or rule out prostate cancer.',
    content: {
      whatItMeasures: `Tissue samples examined for cancer presence, Gleason grade (aggressiveness), and extent of involvement. MRI-fusion targeting improves accuracy.`,

      whyOrdered: `Elevated PSA, abnormal prostate exam, suspicious MRI findings (PI-RADS 4-5), or prior atypical biopsy.`,

      resultsInterpretation: `**Benign:** No cancer (doesn't guarantee none exists)
**Gleason 6:** Low-risk - active surveillance often appropriate
**Gleason 7:** Intermediate risk - treatment usually recommended
**Gleason 8-10:** High-risk - treatment and staging needed`,

      nextSteps: `Low-grade cancer may be observed. Higher grades need treatment discussion and staging. Post-biopsy: blood in fluids is normal; watch for fever.`,

      relatedConditions: [
        { title: 'Prostate Cancer (Screening & Localized Disease)', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { title: 'Understanding Active Surveillance for Prostate Cancer', articleId: '86764b75-bfe3-4065-9c7a-23f1c8d5dd25' },
        { title: 'Prostate Cancer Treatment Options', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' }
      ]
    }
  },

  {
    id: 'uroflow-pvr',
    title: 'Uroflowmetry with Post-Void Residual',
    icon: 'activity',
    category: 'Procedure',
    summary: 'Non-invasive test measuring urine flow rate and bladder emptying to evaluate obstruction or bladder function.',
    content: {
      whatItMeasures: `**Uroflowmetry:** Maximum flow rate (Qmax), flow pattern, voided volume
**Post-void residual (PVR):** Urine remaining after voiding measured by ultrasound`,

      whyOrdered: `BPH evaluation, voiding symptoms assessment, before BPH surgery, after incontinence surgery, and monitoring treatment response.`,

      resultsInterpretation: `**Normal:** Qmax >15 mL/sec (men), PVR <50 mL
**Low Qmax:** Suggests obstruction or weak bladder
**High PVR:** Incomplete emptying - obstruction or bladder weakness
**Bell-shaped curve:** Normal; Plateau curve suggests stricture`,

      nextSteps: `Low flow with normal PVR: Medical therapy reasonable. Low flow with high PVR: Higher priority for treatment. Very high PVR may need catheterization.`,

      relatedConditions: [
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'Urinary Retention', articleId: '3389df5b-d448-4779-8d46-870a74cf8671' },
        { title: 'Urethral Stricture', articleId: 'bd398644-5720-4c8b-8ea2-2265ea628c01' }
      ]
    }
  },

  {
    id: 'renal-mass-biopsy',
    title: 'Renal Mass Biopsy',
    icon: 'crosshair',
    category: 'Procedure',
    summary: 'Procedure obtaining tissue from a kidney mass to determine if cancerous and guide treatment.',
    content: {
      whatItMeasures: `Determines if mass is benign (oncocytoma, AML) vs malignant (RCC subtypes and grade). Diagnostic in 80-90% of cases.`,

      whyOrdered: `Small renal mass before surveillance decision, patient unfit for surgery, suspected metastasis to kidney, before ablation therapy.`,

      resultsInterpretation: `**Benign (oncocytoma, AML):** May observe - avoid unnecessary surgery
**Clear cell RCC:** Most common cancer - treatment based on size/grade
**Non-diagnostic:** May need repeat biopsy or clinical judgment`,

      nextSteps: `Benign masses often observed. RCC treatment depends on size, grade, and patient factors. Non-diagnostic results require clinical correlation.`,

      relatedConditions: [
        { title: 'Renal Cysts and Small Renal Masses', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' },
        { title: 'Renal Mass Biopsy: Understanding the Role, Risks, and Benefits', articleId: '9d448148-3879-4e58-8284-3534aa0ebbc7' }
      ]
    }
  },

  {
    id: 'post-vasectomy-semen-analysis',
    title: 'Post-Vasectomy Semen Analysis',
    icon: 'microscope',
    category: 'Fertility Test',
    summary: 'Semen test after vasectomy confirming procedure success and absence of sperm.',
    content: {
      whatItMeasures: `Checks for azoospermia (no sperm) or rare non-motile sperm. Done 8-16 weeks post-vasectomy after ~20 ejaculations.`,

      whyOrdered: `Mandatory to confirm vasectomy success before discontinuing other contraception. Without testing, cannot guarantee sterility.`,

      resultsInterpretation: `**Azoospermia:** Success - can discontinue other contraception
**Rare non-motile sperm (<100,000/mL):** Generally cleared
**Motile sperm:** NOT cleared - continue contraception and retest`,

      nextSteps: `Cleared patients can stop other birth control. Persistent motile sperm may indicate failure requiring repeat vasectomy.`,

      relatedConditions: [
        { title: 'Understanding Your Semen Analysis', articleId: 'f7df9ddd-3594-423a-be24-079c97ffdcc9' },
        { title: 'Male Infertility: Causes, Evaluation & Treatment', articleId: 'b966fdc4-6310-48da-ad3c-fca8c711c555' }
      ]
    }
  },

  {
    id: 'sti-panel',
    title: 'STI Testing Panel',
    icon: 'test-tube',
    category: 'Urine Test',
    summary: 'Panel of tests detecting sexually transmitted infections causing urinary symptoms or discharge.',
    content: {
      whatItMeasures: `Tests for Chlamydia, Gonorrhea, Syphilis, HIV, Herpes, Trichomonas, Hepatitis B/C, and Mycoplasma using urine, blood, or swab samples.`,

      whyOrdered: `Urethral discharge, dysuria, genital ulcers, epididymitis in young men, new sexual partner, or partner with STI.`,

      resultsInterpretation: `**Negative:** No evidence of tested infections (window period may cause false negatives)
**Positive:** Requires treatment, partner notification, and testing for other STIs`,

      nextSteps: `Appropriate antibiotics for bacterial STIs. Partner notification and treatment essential. Test of cure for some infections. Prevention counseling.`,

      relatedConditions: [
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' }
      ]
    }
  }
];

export default testsData;
